import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import IcomoonReact from 'icomoon-react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Text from '../../../shared/components/Text/Text';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  clearOrderOverrides,
  setOrderingInfoModalOpen,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const IconCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalText = styled('div')`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const WarningText = styled('div')`
  padding: 12px;
  background: ${({ theme }) => theme.colors.warningSurface};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  color: ${({ theme }) => theme.colors.warningText};
  p { color: inherit; }
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  justify-content: center;
`;

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeleteButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2C2C2C;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const CloseButton = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;
`;

const ConfirmResetOrderModal = ({ closeModal }) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const subTitleDate = moment(modalData?.endpointFormattedDate).format('MMM-DD-YYYY');

  const handleResetOrder = () => {
    dispatch(clearOrderOverrides(
      currentUser,
      modalData?.endpointFormattedDate,
      modalData?.endpointFormattedDate,
    ));
    closeModal();
  };

  return (
    <ModalContainer>
      <CloseButton onClick={closeModal}>
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color={colors.textSecondary}
        />
      </CloseButton>
      <IconCircle>
        <IcomoonReact
          iconSet={iconSet}
          size={32}
          icon='restart'
          color='#DB3030'
        />
      </IconCircle>

      <ModalText>
        <Text fontSize='18px'>{subTitleDate}</Text>
        <Text fontSize='20px' fontWeight={600}>
          Reset to Default Order
        </Text>
      </ModalText>

      <WarningText style={{ marginBottom: 16 }}>
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='warning'
          color='#856404'
          style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
        />
        <Text fontSize='14px' color='#856404'>
          Warning: This action cannot be undone
        </Text>
      </WarningText>
      <Text style={{ marginBottom: '20px', cursor: 'pointer', color: colors.link }} onClick={() => dispatch(setOrderingInfoModalOpen(true))}>Click here to learn more about the new reordering functionality</Text>
      <ButtonsContainer>
        <DeleteButton onClick={handleResetOrder}>
          RESET
        </DeleteButton>
        <CancelButton onClick={closeModal}>
          CANCEL
        </CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ConfirmResetOrderModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ConfirmResetOrderModal;
